import type { ApiSpec, AuthScheme, Operation } from './apiSpec';

/**
 * A code sample for one operation, in the three languages the API tab offers
 * (specs/api-docs.md AC-10): the server the definition names, the path with
 * its parameters as placeholders, the query, header and cookie parameters it
 * requires, a placeholder for what lets the caller in, and the body — the
 * definition's example, else a skeleton of its schema. Nothing in it is a
 * value the definition did not give.
 */

export type SampleLanguage = 'python' | 'typescript' | 'curl';

type Request = {
  method: string;
  url: string;
  headers: [string, string][];
  body?: unknown;
};

/**
 * The schemes the sample authenticates with: those one call of the operation
 * sends together — its own requirement, else the API's. An operation that
 * lists none is open, and gets none; so is one that says nothing in a
 * contract whose security is stated per operation. Only a contract that
 * states no security anywhere falls back to the first scheme it declares.
 */
export function schemesFor(spec: ApiSpec, op: Operation): AuthScheme[] {
  const fallback =
    spec.authNote !== 'byOperation' && spec.auth[0] ? [spec.auth[0].name] : [];
  const names = op.requirement ?? spec.requirement ?? fallback;
  return names.map(
    name =>
      spec.schemes[name] ?? {
        name,
        kind: 'other' as const,
        label: name,
        undeclared: true,
      },
  );
}

function requestOf(spec: ApiSpec, op: Operation): Request {
  const base = (spec.server ?? '').replace(/\/+$/, '');
  const query: string[] = [];
  const cookies: string[] = [];
  const headers: [string, string][] = [];
  // Two schemes of one requirement may both be bearer tokens; one header.
  const add = (name: string, value: string) => {
    if (!headers.some(([n]) => n.toLowerCase() === name.toLowerCase())) {
      headers.push([name, value]);
    }
  };

  for (const scheme of schemesFor(spec, op)) {
    if (scheme.kind === 'basic') {
      add('Authorization', 'Basic <base64 of user:password>');
    } else if (scheme.kind === 'apiKey') {
      // A key with nowhere named to put it cannot be placed; it is not
      // guessed into the Authorization header.
      if (!scheme.paramName) continue;
      if (scheme.in === 'query') query.push(`${scheme.paramName}=<api-key>`);
      else if (scheme.in === 'cookie') {
        cookies.push(`${scheme.paramName}=<api-key>`);
      } else add(scheme.paramName, '<api-key>');
    } else if (scheme.kind !== 'other') {
      add('Authorization', 'Bearer <token>');
    }
  }
  for (const p of op.parameters) {
    if (p.in === 'query') query.push(`${p.name}=<${p.name}>`);
    else if (p.in === 'cookie') cookies.push(`${p.name}=<${p.name}>`);
    else add(p.name, `<${p.name}>`);
  }
  if (cookies.length) add('Cookie', cookies.join('; '));
  if (op.body?.value !== undefined) add('Content-Type', op.body.mediaType);

  const path = op.path.replace(/\{([^}]+)\}/g, '<$1>');
  const url = `${base}${path}${query.length ? `?${query.join('&')}` : ''}`;
  return { method: op.method, url, headers, body: op.body?.value };
}

const shellQuote = (s: string) => `'${s.replace(/'/g, `'\\''`)}'`;

function curlOf(r: Request): string {
  // curl sends GET by itself, and POST once there is a body; anything else,
  // and a GET that carries a body, has to be said.
  const explicit = r.method !== 'GET' || r.body !== undefined;
  const lines = [
    `curl${explicit ? ` -X ${r.method}` : ''} ${shellQuote(r.url)}`,
  ];
  for (const [name, value] of r.headers) {
    lines.push(`  -H ${shellQuote(`${name}: ${value}`)}`);
  }
  if (r.body !== undefined) {
    lines.push(`  -d ${shellQuote(JSON.stringify(r.body, null, 2))}`);
  }
  return lines.join(' \\\n');
}

/** A JSON value as a Python literal: True, False and None, not JSON's words. */
export function pythonLiteral(value: unknown, indent = 0): string {
  const pad = '    '.repeat(indent + 1);
  const end = '    '.repeat(indent);
  if (value === null || value === undefined) return 'None';
  if (value === true) return 'True';
  if (value === false) return 'False';
  if (typeof value === 'string') return JSON.stringify(value);
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) {
    if (!value.length) return '[]';
    return `[\n${value
      .map(v => `${pad}${pythonLiteral(v, indent + 1)},`)
      .join('\n')}\n${end}]`;
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (!entries.length) return '{}';
  return `{\n${entries
    .map(
      ([k, v]) =>
        `${pad}${JSON.stringify(k)}: ${pythonLiteral(v, indent + 1)},`,
    )
    .join('\n')}\n${end}}`;
}

function pythonOf(r: Request): string {
  const json = r.headers.some(
    ([name, value]) => name === 'Content-Type' && /json/i.test(value),
  );
  // requests sets the JSON content type itself when given json=.
  const headers = r.headers.filter(
    ([name]) => !(json && name === 'Content-Type'),
  );
  const args = [`    ${JSON.stringify(r.url)},`];
  if (headers.length) {
    args.push(`    headers=${pythonLiteral(Object.fromEntries(headers), 1)},`);
  }
  if (r.body !== undefined) {
    args.push(`    json=${pythonLiteral(r.body, 1)},`);
  }
  return [
    'import requests',
    '',
    `response = requests.${r.method.toLowerCase()}(`,
    ...args,
    ')',
    'response.raise_for_status()',
    'print(response.json())',
  ].join('\n');
}

const tsKey = (name: string) =>
  /^[A-Za-z_$][\w$]*$/.test(name) ? name : JSON.stringify(name);

function typescriptOf(r: Request): string {
  const init: string[] = [`  method: ${JSON.stringify(r.method)},`];
  if (r.headers.length) {
    init.push('  headers: {');
    for (const [name, value] of r.headers) {
      init.push(`    ${tsKey(name)}: ${JSON.stringify(value)},`);
    }
    init.push('  },');
  }
  if (r.body !== undefined) {
    const body = JSON.stringify(r.body, null, 2).replace(/\n/g, '\n  ');
    init.push(`  body: JSON.stringify(${body}),`);
  }
  return [
    `const response = await fetch(${JSON.stringify(r.url)}, {`,
    ...init,
    '});',
    'if (!response.ok) {',
    '  throw new Error(`${response.status} ${response.statusText}`);',
    '}',
    'console.log(await response.json());',
  ].join('\n');
}

/**
 * The sample for one operation of an API, in each language; undefined for an
 * operation no sample can be written for.
 */
export function samplesOf(
  spec: ApiSpec,
  op: Operation,
): Record<SampleLanguage, string> | undefined {
  try {
    const request = requestOf(spec, op);
    return {
      python: pythonOf(request),
      typescript: typescriptOf(request),
      curl: curlOf(request),
    };
  } catch {
    return undefined;
  }
}
